/** Chile — consistent with Podscope audience and es-CL copy elsewhere. */
const CHILE_TZ = 'America/Santiago';

function parseValidDate(updatedAt: string | undefined): Date | null {
    if (!updatedAt) return null;
    const d = new Date(updatedAt);
    return Number.isNaN(d.getTime()) ? null : d;
}

function pickPart(parts: Intl.DateTimeFormatPart[], type: string): string {
    return parts.find(p => p.type === type)?.value ?? '';
}

/**
 * Single chip line: "Actualizado a las HH:MM el 27 de marzo" (hora Chile).
 */
export function formatLiveRankingsChipLabel(updatedAt: string | undefined): string {
    const d = parseValidDate(updatedAt);
    if (!d) return '';

    const dateParts = new Intl.DateTimeFormat('es-CL', {
        timeZone: CHILE_TZ,
        day: 'numeric',
        month: 'long',
    }).formatToParts(d);

    const day = pickPart(dateParts, 'day');
    const month = pickPart(dateParts, 'month');

    const timeParts = new Intl.DateTimeFormat('es-CL', {
        timeZone: CHILE_TZ,
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    }).formatToParts(d);

    const hour = pickPart(timeParts, 'hour');
    const minute = pickPart(timeParts, 'minute');
    const timeStr = `${hour}:${minute}`;

    return `Actualizado a las ${timeStr} el ${day} de ${month}`;
}

/** Stream go-live moment in Chile: "24 abr, 14:35". Empty input → "—". */
export function formatLivestreamStartedChile(iso: string | null): string {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';

    const dateParts = new Intl.DateTimeFormat('es-CL', {
        timeZone: CHILE_TZ,
        day: 'numeric',
        month: 'short',
    }).formatToParts(d);

    const day = pickPart(dateParts, 'day');
    let month = pickPart(dateParts, 'month');
    month = month.replace(/\.$/, '').trim();

    const timeParts = new Intl.DateTimeFormat('es-CL', {
        timeZone: CHILE_TZ,
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    }).formatToParts(d);

    const hour = pickPart(timeParts, 'hour');
    const minute = pickPart(timeParts, 'minute');
    return `${day} ${month}, ${hour}:${minute}`;
}

/**
 * Elapsed time on air since `startedAtIso` until `referenceMs` (e.g. API `updatedAt`),
 * as `HH:MM` (24h-style: total whole hours, zero-padded; minutes zero-padded).
 * Seconds are not shown. Invalid / missing inputs → "—".
 */
export function formatOnAirDuration(
    startedAtIso: string | null,
    referenceMs: number | null
): string {
    if (!startedAtIso || referenceMs == null || !Number.isFinite(referenceMs)) return '—';
    const startMs = new Date(startedAtIso).getTime();
    if (Number.isNaN(startMs)) return '—';
    let elapsedSec = Math.floor((referenceMs - startMs) / 1000);
    if (elapsedSec < 0) elapsedSec = 0;

    const pad2 = (n: number) => String(n).padStart(2, '0');
    const totalMinutes = Math.floor(elapsedSec / 60);
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    return `${pad2(h)}:${pad2(m)}`;
}

/**
 * Short line: start in Chile + time on air at `referenceMs` (e.g. rankings `updatedAt`).
 * Example: "Empezó el 24 abr 2026, 14:35 · 2h 15m al aire".
 * If `referenceMs` is missing/invalid, only the start line is shown (no "al aire" segment).
 */
export function formatLiveTransmissionAirSentence(
    startedAtIso: string | null,
    referenceMs: number | null
): string {
    if (!startedAtIso) return '—';
    const d = new Date(startedAtIso);
    if (Number.isNaN(d.getTime())) return '—';

    const dateParts = new Intl.DateTimeFormat('es-CL', {
        timeZone: CHILE_TZ,
        day: 'numeric',
        month: 'short',
        year: 'numeric',
    }).formatToParts(d);
    const day = pickPart(dateParts, 'day');
    let month = pickPart(dateParts, 'month');
    month = month.replace(/\.$/, '').trim();
    const year = pickPart(dateParts, 'year');
    const fechaCorta = `${day} ${month} ${year}`;

    const timeParts = new Intl.DateTimeFormat('es-CL', {
        timeZone: CHILE_TZ,
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    }).formatToParts(d);
    const hour = pickPart(timeParts, 'hour');
    const minute = pickPart(timeParts, 'minute');
    const timeStr = `${hour}:${minute}`;

    if (referenceMs == null || !Number.isFinite(referenceMs)) {
        return `Empezó el ${fechaCorta}, ${timeStr}`;
    }

    const startMs = d.getTime();
    let elapsedSec = Math.floor((referenceMs - startMs) / 1000);
    if (elapsedSec < 0) elapsedSec = 0;
    const totalMinutes = Math.floor(elapsedSec / 60);
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;

    let dur: string;
    if (totalMinutes < 1) {
        dur = '<1m';
    } else if (h === 0) {
        dur = `${m}m`;
    } else if (m === 0) {
        dur = `${h}h`;
    } else {
        dur = `${h}h ${m}m`;
    }

    return `Empezó el ${fechaCorta}, ${timeStr} · ${dur} al aire`;
}
